import { TaskPhaseStepper } from './TaskPhaseStepper';
import { TaskAutomationCard } from './TaskAutomationCard';
import { TaskProcedureLocksCard } from './TaskProcedureLocksCard';
import { TaskTimeline } from './TaskTimeline';
import { TaskCommentsCard } from './TaskCommentsCard';
import { TaskReviewHeader } from './TaskReviewHeader';
import { TaskListNavigation } from './TaskListNavigation';
import { TaskReviewMainColumn } from './TaskReviewMainColumn';
import { useTaskAgentModule } from './useTaskAgentModule';
import type { TaskReviewLoadedProps } from './TaskReviewLoadedProps';
import { TaskReviewSidePanel } from './TaskReviewSidePanel';
import { taskTimelineViewProps } from './taskTimelineViewProps';
import { useTaskComments } from 'src/pages/authenticated/inbox/hooks/use-task-comments';
import { useTaskViewState } from './view/useTaskViewState';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from 'src/components/ui/resizable';

/**
 * Wide two-column review used on `/tasks/$taskId`. Also hosts Nami's `tasks`
 * chat module for the task, so the agent panel beside the page works this
 * task and resumes any conversation Nami started about it.
 */
export function TaskReviewPageLayout(
  props: TaskReviewLoadedProps & { openChat: boolean },
) {
  const { openTaskChat } = useTaskAgentModule({
    task: props.task,
    openChat: props.openChat,
  });
  const {
    task,
    taskType,
    typeName,
    typeNameById,
    typeNameByKey,
    subtasks,
    rollup,
    users,
    agents,
    userNameById,
    agentNameById,
    currentUserId,
    fromPage,
    trailing,
    timeline,
  } = props;
  const view = useTaskViewState(task, taskType);
  const comments = useTaskComments(task.id);
  const notesCount = comments.isLoading
    ? null
    : (comments.data?.comments.length ?? 0);
  const activityCount = timeline.isLoading ? null : timeline.total;
  // A document or email beside the fields needs the width a 3:2 split
  // does not give it. The handle lets the reviewer adjust from there.
  const sideDefaultSize = view.asideRegions.length > 0 ? 50 : 40;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <TaskReviewHeader
        task={task}
        taskType={taskType}
        typeName={typeName}
        users={users}
        agents={agents}
        fromPage={fromPage}
        trailing={
          <TaskListNavigation taskId={task.id}>{trailing}</TaskListNavigation>
        }
        hideTitle
      />

      <TaskPhaseStepper rollup={rollup} typeNameByKey={typeNameByKey} />

      <TaskAutomationCard taskId={task.id} typeNameByKey={typeNameByKey} />

      <TaskProcedureLocksCard taskId={task.id} />

      <ResizablePanelGroup
        direction="horizontal"
        className="min-h-0 flex-1"
        autoSaveId="task-review-columns"
        data-testid="task-review-columns"
      >
        <ResizablePanel
          defaultSize={100 - sideDefaultSize}
          minSize={30}
          className="flex min-h-0 flex-col"
        >
          {/* The panel sets overflow:hidden inline, which beats a utility
              class on the panel itself. Scroll the column inside it so a
              long task type view stays reachable. */}
          <div
            className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1"
            data-testid="task-review-main-scroll"
          >
            <TaskReviewMainColumn
              task={task}
              taskType={taskType}
              view={view}
              subtasks={subtasks}
              typeNameById={typeNameById}
              fromPage={fromPage}
              agentNameById={agentNameById}
              onContinueInChat={openTaskChat}
            />
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel
          defaultSize={sideDefaultSize}
          minSize={25}
          className="flex min-h-0 flex-col gap-2 overflow-hidden pl-1"
        >
          <TaskReviewSidePanel
            view={view}
            notesCount={notesCount}
            activityCount={activityCount}
            notes={
              <TaskCommentsCard
                key={task.id}
                taskId={task.id}
                className="min-h-0 flex-1"
                heading={null}
                currentUserId={currentUserId}
                userNameById={userNameById}
                agentName={
                  task.assigneeAgentDefinitionId
                    ? agentNameById.get(task.assigneeAgentDefinitionId)
                    : undefined
                }
              />
            }
            timeline={
              <TaskTimeline
                {...taskTimelineViewProps({
                  className: 'min-h-0 flex-1',
                  taskId: task.id,
                  timeline,
                  userNameById,
                  agentNameById,
                })}
              />
            }
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
